import tempMee from '../apis/tempMee';
import { REFERRAL_ACTIONS, SHOW_MESSAGE } from './actionTypes';
import { mapReferrals } from '../domain/Referrals/mappers';

export const REFERRAL_LIST_ENDPOINT = '/referral-service/admin/referrals';

// Two quick filter changes can race: whichever response lands LAST would win
// regardless of which request was newest. A module-level sequence number,
// bumped per `fetchReferrals` call, lets each response check it is still the
// latest and drop itself otherwise.
let requestSequence = 0;

// All selects send their 'all' sentinel through verbatim; referral-service
// treats an omitted filter as "any", so 'all' is dropped rather than forwarded.
const REFERRER_TYPE_TO_PARAM = {
  HYG: 'professional',
  DNT: 'office',
};

/** Fetches the Referral list from referral-service's admin read endpoint. */
export const fetchReferrals = (state) => async (dispatch, getState) => {
  let sequence;

  try {
    let newSearchParams = {};
    if (state) {
      newSearchParams = {
        page: state?.page || 1,
        limit: 15,
        status: state?.status && state.status !== 'all' ? state.status : '',
        referrerType:
          state?.referrerType && state.referrerType !== 'all' ? state.referrerType : '',
        program: state?.program && state.program !== 'all' ? state.program : '',
        isCancelled: state?.isCancelled && state.isCancelled !== 'all' ? state.isCancelled : '',
        searchText: state?.searchText || '',
      };
    }

    const { searchParams } = getState().referral;
    if (JSON.stringify(searchParams) === JSON.stringify(newSearchParams) && !state?.refresh) {
      return;
    }

    // Claim this fetch's sequence number now, at the point it actually goes
    // out — not earlier, so a dedup'd no-op above never advances it.
    requestSequence += 1;
    sequence = requestSequence;

    dispatch({
      type: REFERRAL_ACTIONS.GET_REFERRAL_REQUEST,
      payload: newSearchParams,
    });

    const params = { page: newSearchParams.page, limit: newSearchParams.limit };
    if (newSearchParams.searchText) params.search = newSearchParams.searchText;
    if (newSearchParams.status) params.status = newSearchParams.status;
    if (newSearchParams.referrerType) {
      params.referrerType =
        REFERRER_TYPE_TO_PARAM[newSearchParams.referrerType] ?? newSearchParams.referrerType;
    }
    if (newSearchParams.program) params.program = newSearchParams.program;
    if (newSearchParams.isCancelled) params.isCancelled = newSearchParams.isCancelled;

    const response = await tempMee.get(REFERRAL_LIST_ENDPOINT, {
      params,
    });

    // A newer fetchReferrals call has gone out since this one was issued —
    // that one owns the store now, so this stale response is dropped.
    if (sequence !== requestSequence) return;

    dispatch({
      type: REFERRAL_ACTIONS.GET_REFERRAL_SUCCESS,
      payload: {
        data: mapReferrals(response.data?.referrals),
        total: response.data?.total,
      },
    });
  } catch (err) {
    // Same staleness guard on the failure path: an older request's error
    // must not clobber a newer request's already-applied (or in-flight)
    // result.
    if (sequence !== undefined && sequence !== requestSequence) return;
    dispatch({ type: REFERRAL_ACTIONS.GET_REFERRAL_FAILURE, payload: err });
  }
};

// The monolith owns cancel — referral-service does not own writes yet
// (GOTU-7245). It is offered only for legacy office->office rows, keyed by
// the migrated recipient's monolith id (`migratedFrom`).
export const cancelReferral = (id) => async (dispatch) => {
  try {
    dispatch({ type: REFERRAL_ACTIONS.CANCEL_REFERRAL_REQUEST });

    const response = await tempMee.patch(
      `/admin/referral/${id}`,
      { isCancelled: true },
    );

    dispatch({ type: REFERRAL_ACTIONS.CANCEL_REFERRAL_SUCCESS, payload: response.data });

    dispatch({
      type: SHOW_MESSAGE,
      payload: { message: 'The referral has been successfully cancelled.' },
    });
  } catch (err) {
    dispatch({ type: REFERRAL_ACTIONS.CANCEL_REFERRAL_FAILURE, payload: err });
  }
};
